import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';

/**
 * useAchAgreement — the on-screen "paying us by ACH" form.
 *
 * ⚠ The agreement TEXT comes from the server on every load and is never held
 * in the client. That is deliberate: the signature has to attest to the words
 * the server produced, and a client copy of legal text is a copy that can
 * drift from the one the PDF renders. Render what `agreement` gives you.
 */

export interface AchAgreementSection {
  heading: string;
  fields: Array<{ label: string; value: string }>;
}

export interface AchAgreementText {
  version: string;
  headline: string;
  intro: string;
  sections: AchAgreementSection[];
  acknowledgment: string[];
  footnote: string;
}

export interface AchAgreementSigned {
  id: string | null;
  signed_at: string;
  signed_at_label: string;
  /** Parsed server-side from the filed row — never picked out of `notes` here. */
  signed_by: string | null;
  signed_title: string | null;
  version: string | null;
  notes: string | null;
  file_name: string | null;
  /** Short-lived signed URL to the PDF; null when it could not be stored. */
  url: string | null;
}

export interface AchAgreementResponse {
  ok: true;
  agreement: AchAgreementText;
  signed: AchAgreementSigned | null;
}

export function useAchAgreement(enabled = true) {
  return useQuery<AchAgreementResponse>({
    queryKey: ['ach-agreement'],
    queryFn: () => adminFetch('payee-ach-agreement'),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useSignAchAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { signature: string; signed_by: string; title?: string }) =>
      adminFetch<{
        ok: true;
        signed: AchAgreementSigned;
        version: string;
        notes?: string[];
      }>('payee-ach-agreement', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ach-agreement'] });
      // Staff read the same row on the customer's Account documents card —
      // the key is per-customer, so invalidate the whole admin docs subtree.
      qc.invalidateQueries({ queryKey: ['admin', 'customer-documents'] });
    },
  });
}
